import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [permission, setPermission] = useState(Notification.permission);
  const [subscription, setSubscription] = useState(null);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported('serviceWorker' in navigator && 'PushManager' in window);
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return false;

    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return false;

      // Get VAPID key
      const { data } = await axios.get(`${API_URL}/push/vapid-public-key`);
      if (!data.publicKey) return false;

      // Register/get service worker
      const registration = await navigator.serviceWorker.ready;

      // Subscribe
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey)
      });

      // Send to server
      const subJson = sub.toJSON();
      await axios.post(`${API_URL}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      });

      setSubscription(sub);
      return true;
    } catch (err) {
      console.error('Push subscription failed:', err);
      return false;
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!subscription) return;
    try {
      const subJson = subscription.toJSON();
      await axios.delete(`${API_URL}/push/unsubscribe`, {
        data: { endpoint: subJson.endpoint, keys: subJson.keys }
      });
      await subscription.unsubscribe();
      setSubscription(null);
    } catch (err) {
      console.error('Unsubscribe failed:', err);
    }
  }, [subscription]);

  return { permission, subscription, supported, subscribe, unsubscribe };
}
